import { useEffect, useMemo, useState } from "react";
import { api, day } from "./api.js";
import { shortName } from "./company/common.jsx";
import { roleName } from "./company/Company.jsx";

// Кабінет користувача (#/account/<розділ>): профіль, компанії з доступом, тариф і оплата — рахунок на оплату (виписується
// й автоматично) та оплата карткою monobank (server/src/modules/account.js)
const TABS = [["profile", "Профіль"], ["companies", "Компанії"], ["billing", "Тариф і оплата"]];
const uah = (n) => `${Number(n).toLocaleString("uk-UA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;
const ACCESS = { trial: ["Пробний період", "badge-info"], paid: ["Оплачено", "badge-ok"], due: ["Потрібна оплата", "badge-bad"] };

export default function Account({ user, tab = "profile", onUser }) {
  const cur = TABS.some(([k]) => k === tab) ? tab : "profile";
  return (
    <section className="account">
      <div className="section-head"><div><h1>Мій кабінет</h1><p className="muted">{user.name ? `${user.name} · ` : ""}{user.email}</p></div></div>
      <div className="tabs reports-tabs" role="tablist" aria-label="Кабінет">
        {TABS.map(([k, l]) => <a key={k} role="tab" aria-selected={cur === k} className={`tab${cur === k ? " active" : ""}`} href={`#/account/${k}`}>{l}</a>)}
      </div>
      {cur === "profile" && <Profile user={user} onUser={onUser} />}
      {cur === "companies" && <Companies />}
      {cur === "billing" && <Billing />}
    </section>
  );
}

function Profile({ user, onUser }) {
  const [name, setName] = useState(user.name || ""), [st, setSt] = useState("");
  const [pw, setPw] = useState({ current: "", next: "" }), [pst, setPst] = useState("");
  const save = async (e) => { e.preventDefault(); setSt("зберігаємо…"); try { await api("/api/account/profile", { method: "PUT", body: { name } }); onUser?.({ ...user, name: name.trim() || null }); setSt("збережено"); } catch (x) { setSt(x.message); } };
  const change = async (e) => { e.preventDefault(); setPst("змінюємо…"); try { await api("/api/account/password", { method: "POST", body: pw }); setPw({ current: "", next: "" }); setPst("пароль змінено"); } catch (x) { setPst(x.message); } };
  return (
    <div className="account-grid">
      <form className="card" onSubmit={save}>
        <h2 className="h3">Профіль</h2>
        <label className="field">Ім'я та прізвище<input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} autoComplete="name" /></label>
        <label className="field">Email<input value={user.email} readOnly /></label>
        <div className="actions"><button className="btn">Зберегти</button><span className="muted small" role="status">{st}</span></div>
      </form>
      <form className="card" onSubmit={change}>
        <h2 className="h3">Пароль</h2>
        <label className="field">Поточний пароль<input type="password" value={pw.current} autoComplete="current-password" required onChange={(e) => setPw({ ...pw, current: e.target.value })} /></label>
        <label className="field">Новий пароль<input type="password" value={pw.next} autoComplete="new-password" minLength={12} required onChange={(e) => setPw({ ...pw, next: e.target.value })} />
          <span className="field-hint">Щонайменше 12 символів</span></label>
        <div className="actions"><button className="btn btn-secondary">Змінити пароль</button><span className="muted small" role="status">{pst}</span></div>
      </form>
    </div>
  );
}

const useBilling = (v) => {
  const [d, setD] = useState(null), [err, setErr] = useState("");
  useEffect(() => { api("/api/account/billing").then(setD, (e) => setErr(e.message)); }, [v]);
  return { d, err };
};

function AccessBadge({ c }) {
  const [label, cls] = ACCESS[c.state] || ["—", "badge-idle"];
  return <span className={`badge ${cls}`}>{label}{c.until ? ` ${c.state === "due" ? "з" : "до"} ${day(c.until)}` : ""}</span>;
}

function Companies() {
  const { d, err } = useBilling(0);
  if (err) return <p className="error">{err}</p>;
  if (!d) return <p className="muted" role="status">Завантаження…</p>;
  return (
    <div className="card">
      <h2 className="h3">Мої компанії</h2>
      {!d.companies.length ? <p className="empty">Компаній ще немає. <a href="#/add">Додати компанію ключем КЕП</a></p> : (
        <ul className="account-cos">{d.companies.map((c) => (
          <li key={c.id}><span className="account-ava" aria-hidden="true">{(shortName(c.name) || c.edrpou).replace(/^ТОВ\s*«?/, "").slice(0, 2).toUpperCase()}</span>
            <span className="account-co"><a href={`#/c/${c.id}`}><b>{shortName(c.name) || c.edrpou}</b></a><span className="muted small">ЄДРПОУ {c.edrpou} · {roleName[c.role]}</span></span>
            <AccessBadge c={c} /></li>))}</ul>)}
      <p className="muted small"><a href="#/add">+ Додати компанію</a></p>
    </div>
  );
}

function Billing() {
  const [v, setV] = useState(0);
  const { d, err } = useBilling(v);
  const owned = useMemo(() => (d?.companies || []).filter((c) => c.role === "owner"), [d]);
  const [pick, setPick] = useState(null), [months, setMonths] = useState(1), [payer, setPayer] = useState(""), [st, setSt] = useState(""), [busy, setBusy] = useState(false);
  useEffect(() => { if (d && pick === null) { setPick(owned.filter((c) => c.state !== "paid").map((c) => c.id)); setPayer(owned[0]?.id || ""); } }, [d]);
  if (err) return <p className="error">{err}</p>;
  if (!d) return <p className="muted" role="status">Завантаження…</p>;
  const sel = pick || [];
  const total = d.price ? (months === 12 && d.priceYear ? d.priceYear : d.price * months) * sel.length : 0;
  const issue = async () => {
    setBusy(true); setSt("");
    try { const r = await api("/api/account/invoices", { method: "POST", body: { companyIds: sel, months, payerCompanyId: payer } }); setV((x) => x + 1); window.open(`/api/account/invoices/${r.invoice.id}/html`, "_blank", "noopener"); }
    catch (x) { setSt(x.message); } finally { setBusy(false); }
  };
  const payCard = async (id) => { try { const r = await api(`/api/account/invoices/${id}/mono`, { method: "POST" }); location.href = r.pageUrl; } catch (x) { setSt(x.message); } };
  return (
    <>
      <div className="card account-plan">
        <div><h2 className="h3">Тариф</h2>
          <p><b>{d.price ? `${uah(d.price)}` : "Ціну ще не встановлено"}</b> за компанію на місяць{d.priceYear ? <> або <b>{uah(d.priceYear)}</b> на рік</> : null}{d.price && d.vat ? ", з ПДВ" : ""}. Перший місяць ({d.trialDays} днів) — безкоштовно.</p>
          <p className="muted small">Рахунок на наступний місяць виписується автоматично за 7 днів до кінця оплаченого або пробного періоду. Оплатити можна карткою (monobank) або з розрахункового рахунку за рахунком.</p></div>
      </div>
      {!d.ready && <p className="notice small">Оплату ще налаштовуємо — до того всі компанії працюють у пробному режимі без обмежень.</p>}
      {owned.length > 0 && (
        <div className="card">
          <h2 className="h3">Оплатити доступ</h2>
          <ul className="account-cos">{owned.map((c) => (
            <li key={c.id}><label className="account-check"><input type="checkbox" checked={sel.includes(c.id)} onChange={(e) => setPick(e.target.checked ? [...sel, c.id] : sel.filter((x) => x !== c.id))} />
              <span className="account-co"><b>{shortName(c.name) || c.edrpou}</b><span className="muted small">ЄДРПОУ {c.edrpou}</span></span></label><AccessBadge c={c} /></li>))}</ul>
          <div className="account-row">
            <label className="field">Період<select value={months} onChange={(e) => setMonths(Number(e.target.value))}>{d.months.map((m) => <option key={m} value={m}>{m === 12 && d.priceYear ? "Рік (12 міс.)" : `${m} міс.`}</option>)}</select></label>
            <label className="field">Платник (рахунок на компанію)<select value={payer} onChange={(e) => setPayer(e.target.value)}>{owned.map((c) => <option key={c.id} value={c.id}>{shortName(c.name) || c.edrpou}</option>)}</select></label>
            <div className="account-total"><span className="muted small">До сплати</span><b>{d.price ? uah(total) : "—"}</b></div>
            <button type="button" className="btn" disabled={!d.ready || !sel.length || !payer || busy} onClick={issue}>{busy ? "Виписуємо…" : "Виписати рахунок"}</button>
          </div>
          {st && <p className="error small" role="alert">{st}</p>}
        </div>
      )}
      <div className="card">
        <h2 className="h3">Рахунки</h2>
        {!d.invoices.length ? <p className="empty">Рахунків ще немає.</p> : (
          <div className="table-wrap" tabIndex={0} role="region" aria-label="Рахунки">
            <table className="table-static">
              <thead><tr><th>Рахунок</th><th>Компанії</th><th className="num">Сума</th><th>Стан</th><th /></tr></thead>
              <tbody>{d.invoices.map((i) => (
                <tr key={i.id}>
                  <td>№ {i.number}<br /><span className="muted small">від {day(i.issuedOn)}{i.auto ? " · автоматично" : ""}</span></td>
                  <td className="wrap-cell small">{i.items.map((x) => `${shortName(x.name)} × ${x.months} міс.`).join(", ")}</td>
                  <td className="num">{uah(i.total)}</td>
                  <td>{i.state === "paid" ? <span className="badge badge-ok">Оплачено {day(i.paidAt)}</span> : i.state === "cancelled" ? <span className="badge badge-idle">Скасовано</span> : <span className="badge badge-warn">Очікує оплати</span>}</td>
                  <td className="account-acts"><a className="btn btn-ghost btn-sm" href={`/api/account/invoices/${i.id}/html`} target="_blank" rel="noopener">Рахунок</a>
                    {i.state === "issued" && d.mono && <button type="button" className="btn btn-sm" onClick={() => payCard(i.id)}>Оплатити карткою</button>}</td>
                </tr>))}</tbody>
            </table>
          </div>)}
      </div>
    </>
  );
}
